import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { colors } from "../../../styles/theme/colors";
import theme from "../../../styles/theme/theme";
import CustomButton from "../Button/Custombutton";
import { DialogWrapper } from "./DialogWrapper";
import acceptIcon from "../../../../assets/accept.svg";
import rejectIcon from "../../../../assets/reject.svg";

interface OfferConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  variant: "accepted" | "rejected";
  travelerName?: string;
  itineraryName?: string;
  onViewTravelers?: () => void;
}

const OfferConfirmationDialog: React.FC<OfferConfirmationDialogProps> = ({
  open,
  onClose,
  variant,
  travelerName = "",
  itineraryName = "",
  onViewTravelers,
}) => {
  const { t } = useTranslation();
  const isAccepted = variant === "accepted";

  const icon = isAccepted ? acceptIcon : rejectIcon;

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)",
          width: { xs: "90%", sm: "512px" },
          maxWidth: "512px",
          margin: { xs: "16px", sm: "32px" },
        },
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
          isolation: "isolate",
          position: "relative",
          zIndex: 1,
        }}
      >

        {/* Icon - decorative, behind corner ripple */}
        {!isAccepted && (
          <Box
            component="img"
            src={icon}
            alt=""
            sx={{
              position: "absolute",
              top: "9px",
              left: "12px",
              width: { xs: "38px", sm: "48px" },
              height: { xs: "38px", sm: "48px" },
              pointerEvents: "none",
              opacity: 0.8,
              zIndex: 0,
            }}
          />
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: { xs: "32px 20px 0px", sm: "40px 24px 0px" },
            gap: "20px",
            width: "100%",
            position: "relative",
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px",
              position: "absolute",
              width: "44px",
              height: "44px",
              right: "16px",
              top: "16px",
              borderRadius: "8px",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <X size={24} color="#A4A7AE" />
          </IconButton>

          {/* Icon inline for accepted variant */}
          {isAccepted && (
            <Box
              component="img"
              src={icon}
              alt="Accept"
              sx={{
                width: "56px",
                height: "56px",
                flexShrink: 0,
              }}
            />
          )}

          {/* Title */}
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontStyle: "normal",
              fontWeight: 600,
              fontSize: { xs: "20px", sm: "24px" },
              lineHeight: "36px",
              textAlign: "center",
              color: "#181D27",
              position: "relative",
              zIndex: 1,
            }}
          >
            {isAccepted
              ? t("offerNotifications.sender.senderConfirmed.title")
              : "Offer Rejected"}
          </Typography>

          {/* Message */}
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: { xs: "14px", sm: "16px" },
              lineHeight: "24px",
              textAlign: "center",
              color: "#515D6F",
              position: "relative",
              zIndex: 1,
              px: 2,
            }}
          >
            {isAccepted
              ? (() => {
                  const message = t("offerNotifications.sender.senderConfirmed.message", {
                    travelerName: travelerName,
                    itemName: itineraryName,
                  });

                  const escapeRegExp = (value: string) =>
                    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

                  const parts = message.split(
                    new RegExp(
                      `(${escapeRegExp(travelerName)}|${escapeRegExp(itineraryName)})`,
                      "g"
                    )
                  );

                  return parts.map((part, index) => {
                    if (part === travelerName || part === itineraryName) {
                      return (
                        <Box
                          key={index}
                          component="span"
                          sx={{ fontWeight: 600, color: "#181D27" }}
                        >
                          {part}
                        </Box>
                      );
                    }
                    return part;
                  });
                })()
              : "The traveler has declined your offer. You can check other travelers who might be interested."}
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: { xs: "24px 20px 20px", sm: "32px 24px 24px" },
            gap: "12px",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          {isAccepted ? (
            <>
              <CustomButton
                buttonText="Okay"
                showButtonText
                onClick={onClose}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 24px",
                  gap: "8px",
                  flex: 1,
                  height: "44px",
                  background: colors.neutral.white,
                  border: `1px solid ${colors.form.border}`,
                  borderRadius: "8px",
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#373940",
                  textTransform: "none",
                  "&:hover": {
                    background: "#F9FAFB",
                  },
                }}
              />
              <CustomButton
                buttonText={t("offerNotifications.sender.senderConfirmed.viewDetails")}
                showButtonText
                onClick={onViewTravelers || onClose}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 24px",
                  gap: "8px",
                  flex: 1,
                  height: "44px",
                  background: colors.traveller.primary,
                  borderRadius: "8px",
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: colors.neutral.white,
                  textTransform: "none",
                  "&:hover": {
                    background: "#7A0056",
                    opacity: 0.9,
                  },
                }}
              />
            </>
          ) : (
            <>
              <CustomButton
                buttonText="Close"
                showButtonText
                onClick={onClose}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 24px",
                  gap: "8px",
                  flex: 1,
                  height: "44px",
                  background: colors.neutral.white,
                  border: `1px solid ${colors.form.border}`,
                  borderRadius: "8px",
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#373940",
                  textTransform: "none",
                  "&:hover": {
                    background: "#F9FAFB",
                  },
                }}
              />
              <CustomButton
                buttonText="View Travelers"
                showButtonText
                onClick={onViewTravelers || onClose}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 24px",
                  gap: "8px",
                  flex: 1,
                  height: "44px",
                  background: colors.traveller.primary,
                  borderRadius: "8px",
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: colors.neutral.white,
                  textTransform: "none",
                  "&:hover": {
                    background: "#7A0056",
                    opacity: 0.9,
                  },
                }}
              />
            </>
          )}
        </Box>
      </Box>
    </DialogWrapper>
  );
};

export default OfferConfirmationDialog;
